import { PERSONAL_SETTINGS_PANELS, SETTINGS_PANELS, type PermissionKey, type SettingsPanel, type WebhookFormatKey } from "@sagot/constants";
import type { IconName } from "@sagot/ui";

export const DEFAULT_PANEL: SettingsPanel = "org";

/** Nav icon per panel (mockup `#settings-tabs`). */
export const PANEL_ICONS: Readonly<Record<SettingsPanel, IconName>> = {
  org: "building",
  profile: "user",
  api: "key",
  webhooks: "webhook",
  integrations: "plug",
  "secure-launch": "shield-check",
  security: "shield",
  privacy: "lock",
  audit: "scroll-text",
  notifications: "bell",
  domains: "globe",
  attributes: "sliders-horizontal",
  sla: "gauge",
};

/** Permission required to see a workspace panel (personal panels need none). */
const PANEL_PERMISSION: Readonly<Partial<Record<SettingsPanel, PermissionKey>>> = {
  org: "settings.view",
  api: "settings.view",
  webhooks: "settings.view",
  integrations: "settings.view",
  "secure-launch": "settings.view",
  security: "settings.view",
  privacy: "settings.view",
  audit: "audit.view",
  domains: "settings.view",
  attributes: "settings.view",
  sla: "settings.view",
};

/** Route segment → panel; unknown or missing → Organization. */
export function resolvePanel(param: string | undefined): SettingsPanel {
  return SETTINGS_PANELS.find((panel) => panel === param) ?? DEFAULT_PANEL;
}

export function panelPermission(panel: SettingsPanel): PermissionKey | null {
  if (PERSONAL_SETTINGS_PANELS.includes(panel)) return null;
  return PANEL_PERMISSION[panel] ?? "settings.view";
}

export function panelAllowed(panel: SettingsPanel, can: (permission: PermissionKey) => boolean): boolean {
  const permission = panelPermission(panel);
  return permission === null || can(permission);
}

/** Panels shown in the nav for this member, in mockup order. */
export function visiblePanels(can: (permission: PermissionKey) => boolean): SettingsPanel[] {
  return SETTINGS_PANELS.filter((panel) => panelAllowed(panel, can));
}

/** Same rule as the API's `normalize_domain`: dotted labels, alphabetic 2-63 char TLD, 253 chars max. */
const DOMAIN = /^(?=.{1,253}$)((?!-)[a-z0-9-]{1,63}(?<!-)\.)+[a-z]{2,63}$/;
const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const URL_SCHEME = /^[a-z][a-z\d+.-]*:\/\//;

export function isDomain(value: string): boolean {
  return DOMAIN.test(value.toLowerCase());
}

/**
 * Typed or pasted text → a domain the API accepts (`example.ph`), or null. Trims, lower-cases and drops a
 * leading `@`, the URL scheme, any path / query / fragment and a trailing dot.
 */
export function normalizeEmailDomain(raw: string): string | null {
  const domain = raw
    .trim()
    .toLowerCase()
    .replace(URL_SCHEME, "")
    .replace(/[/?#].*$/, "")
    .replace(/^@+/, "")
    .replace(/\.$/, "");
  return isDomain(domain) ? domain : null;
}

/** IPv4 address, IPv4 CIDR (/0–/32) or a plain IPv6 / IPv6 CIDR (server validates strictly). */
export function isIpOrCidr(value: string): boolean {
  const [address = "", prefix, extra] = value.split("/");
  if (extra !== undefined) return false;
  if (IPV4.test(address)) return prefix === undefined || (/^\d{1,2}$/.test(prefix) && Number(prefix) <= 32);
  if (address.includes(":") && /^[0-9a-f:]+$/i.test(address)) return prefix === undefined || (/^\d{1,3}$/.test(prefix) && Number(prefix) <= 128);
  return false;
}

/** Comma / whitespace separated list → trimmed, lower-cased, de-duplicated entries. */
export function parseList(text: string): string[] {
  const items = text
    .split(/[\s,;]+/)
    .map((item) => item.trim().toLowerCase().replace(/^@/, ""))
    .filter(Boolean);
  return [...new Set(items)];
}

export function invalidEntries(items: readonly string[], valid: (value: string) => boolean): string[] {
  return items.filter((item) => !valid(item));
}

/** Host of a URL for compact tables ("portal.example.ph"); the raw text when unparsable. */
export function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

const CHAT_WEBHOOKS: Readonly<Record<Exclude<WebhookFormatKey, "sagot">, { hosts: readonly string[]; path: RegExp }>> = {
  discord: { hosts: ["discord.com", "discordapp.com"], path: /^\/api(?:\/v\d{1,2})?\/webhooks\// },
  slack: { hosts: ["hooks.slack.com"], path: /^\/services\// },
};

/** Whether `url` is the app's own incoming-webhook URL; mirrors the backend rule (Sagot JSON accepts any URL). */
export function formatAcceptsUrl(format: WebhookFormatKey, url: string): boolean {
  if (format === "sagot") return true;
  try {
    const parsed = new URL(url.trim());
    const rule = CHAT_WEBHOOKS[format];
    return rule.hosts.includes(parsed.hostname.toLowerCase()) && rule.path.test(parsed.pathname);
  } catch {
    return false;
  }
}

/** The chat format an incoming-webhook URL belongs to (`discord`, `slack`), or `null` for any other URL. */
export function suggestWebhookFormat(url: string): Exclude<WebhookFormatKey, "sagot"> | null {
  if (formatAcceptsUrl("discord", url)) return "discord";
  if (formatAcceptsUrl("slack", url)) return "slack";
  return null;
}

export type DeliveryTone = "success" | "warning" | "danger" | "neutral";

/** Webhook delivery status (`pending|succeeded|failed|retrying`) → badge tone. */
export function deliveryTone(status: string, code: number | null): DeliveryTone {
  if (status === "succeeded" || (code !== null && code >= 200 && code < 300)) return "success";
  if (status === "pending" || status === "retrying") return "warning";
  if (status === "failed") return "danger";
  return "neutral";
}

export function isFailedDelivery(status: string, code: number | null): boolean {
  return deliveryTone(status, code) === "danger";
}

interface CsvRow {
  occurred_at: string;
  actor_name: string | null;
  actor_type: string;
  action: string;
  target_label: string | null;
  target_type: string | null;
  ip: string | null;
}

function csvCell(value: string): string {
  // Neutralise spreadsheet formula injection, then quote.
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** Audit rows → CSV (header + one line per entry), formula-injection safe. */
export function buildAuditCsv(rows: readonly CsvRow[], header: readonly string[], systemLabel: string): string {
  const lines = rows.map((row) =>
    [row.occurred_at, row.actor_name ?? (row.actor_type === "user" ? "" : systemLabel), row.action, row.target_label ?? row.target_type ?? "", row.ip ?? ""].map(csvCell).join(","),
  );
  return [header.map(csvCell).join(","), ...lines].join("\r\n");
}

/** Triggers a client-side download of generated text (CSV, backup codes). */
export function downloadText(filename: string, text: string, type = "text/plain"): void {
  const url = URL.createObjectURL(new Blob([text], { type: `${type};charset=utf-8` }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Workspace deletion confirm: exact name, ignoring surrounding whitespace. */
export function deleteConfirmed(typed: string, workspaceName: string): boolean {
  return typed.trim() === workspaceName.trim() && workspaceName.trim().length > 0;
}

/** "settings.organization_updated" → "settings organization updated" (fallback label). */
export function humanizeAction(action: string): string {
  return action.replace(/[._]+/g, " ").trim();
}
